import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {AddPersonRegularIcon} from 'shells/icon/svg/add-person-regular-icon';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import type {BaseRowStatusBadge} from 'shells/row/base-row/base-row';
import type {ToolbarOption} from 'shells/toolbar/toolbar';

import {analyticsClient} from '../../../../../analytics-client';
import type {NutshellState} from '../../../../../store';
import type {Option} from '../../../../core/select/graphql-select-creatable';
import {CompanyRow} from '../../../../core-entity/rows/peep-rows/company-row';
import type {Props as CompanyRowProps} from '../../../../core-entity/rows/peep-rows/company-row';
import {openSidebar} from '../../../../people-iq/redux/actions';
import * as selectors from '../../../../people-iq/redux/selectors';

import {useGetIqContactsForAccount} from '../graphql/hooks';

interface Props extends CompanyRowProps {
    leadId: string;
    setIsCreatingContact: (arg1: boolean) => void;
    setContactParentAccount: (arg1: Option) => void;
}

export function LeadCompanyRow(props: Props) {
    const {peopleIqContacts, refetch} = useGetIqContactsForAccount(props.accountId);

    const dispatch = useDispatch();
    const hasDismissed = useSelector((state: NutshellState) => {
        return selectors.getHasDismissedForAccount(state, props.accountId);
    });

    const handleIntelligenceClick = () => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_PIQ_SIDEBAR_LEAD_OPENED);

        dispatch(
            openSidebar({
                accountId: props.accountId,
                accountName: props.name || 'Unnamed company',
                leadId: props.leadId,
                contacts:
                    peopleIqContacts && peopleIqContacts.edges
                        ? peopleIqContacts.edges.map((edge) => edge.node)
                        : [],
            })
        );
    };

    // Refetch PeopleIQ contacts when account contacts are added
    useEffect(() => {
        const handleContactsUpdated = () => {
            if (refetch) {
                refetch();
            }
        };

        $('body').on(Nut.events.global.accountContactsWereUpdated, handleContactsUpdated);

        return () => {
            $('body').off(Nut.events.global.accountContactsWereUpdated, handleContactsUpdated);
        };
    }, [refetch]);

    const numberOfPeopleIqResults =
        peopleIqContacts && typeof peopleIqContacts.totalCount === 'number'
            ? peopleIqContacts.totalCount
            : 0;

    const toolbarOptions: ToolbarOption[] = [
        {
            id: 'company-add-person',
            tooltipText: 'Add a person',
            icon: AddPersonRegularIcon,
            onClick: () => {
                props.setContactParentAccount({id: props.accountId, name: props.name || ''});
                props.setIsCreatingContact(true);
            },
        },
    ];

    const statusBadges: BaseRowStatusBadge[] = [];
    if (numberOfPeopleIqResults > 0 && !hasDismissed) {
        statusBadges.push({
            text: `${numberOfPeopleIqResults} ${
                numberOfPeopleIqResults === 1 ? 'person' : 'people'
            } found`,
            level: 'info--purple',
            icon: AiSparkleNewIcon,
            onClick: handleIntelligenceClick,
        });
    }

    return (
        <>
            <CompanyRow
                {...props}
                prependStatusBadges={statusBadges}
                prependToolbarOptions={toolbarOptions}
            />
        </>
    );
}
